import React, { useCallback, useEffect, useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// value for <input type="datetime-local"> in the browser's time zone
const toLocalInput = date => {
	const pad = n => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
		date.getHours()
	)}:${pad(date.getMinutes())}`;
};

const defaultWhen = () => toLocalInput(new Date(Date.now() + 60 * 60 * 1000));

const ScheduleDialog = ({ open, onClose, ticketId }) => {
	const [items, setItems] = useState([]);
	const [body, setBody] = useState("");
	const [when, setWhen] = useState(defaultWhen());
	const [recurrence, setRecurrence] = useState("none");
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		try {
			const { data } = await api.get(`/tickets/${ticketId}/scheduled`);
			setItems(data.scheduled || []);
		} catch (err) {
			toastError(err);
		}
	}, [ticketId]);

	useEffect(() => {
		if (open) {
			setBody("");
			setWhen(defaultWhen());
			setRecurrence("none");
			void load();
		}
	}, [open, load]);

	const save = async () => {
		setSaving(true);
		try {
			await api.post(`/tickets/${ticketId}/scheduled`, {
				body,
				sendAt: new Date(when).toISOString(),
				recurrence,
			});
			toast.success(i18n.t("schedule.saved"));
			setBody("");
			await load();
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	const cancel = async item => {
		try {
			await api.delete(`/tickets/${ticketId}/scheduled/${item.id}`);
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const statusLabel = item => {
		const base = i18n.t(`schedule.status.${item.status}`);
		return item.recurrence && item.recurrence !== "none"
			? `${base} · ${i18n.t(`schedule.recurrence.${item.recurrence}`)}`
			: base;
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>{i18n.t("schedule.title")}</DialogTitle>
			<DialogContent dividers>
				<TextField
					fullWidth
					multiline
					rows={3}
					variant="outlined"
					margin="dense"
					label={i18n.t("schedule.message")}
					value={body}
					onChange={e => setBody(e.target.value)}
					inputProps={{ maxLength: 4000 }}
				/>
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
					<TextField
						type="datetime-local"
						variant="outlined"
						margin="dense"
						label={i18n.t("schedule.when")}
						value={when}
						onChange={e => setWhen(e.target.value)}
						InputLabelProps={{ shrink: true }}
						style={{ flex: "1 1 200px" }}
					/>
					<TextField
						select
						variant="outlined"
						margin="dense"
						label={i18n.t("schedule.repeat")}
						value={recurrence}
						onChange={e => setRecurrence(e.target.value)}
						style={{ flex: "1 1 140px" }}
					>
						{["none", "daily", "weekly", "monthly"].map(r => (
							<MenuItem key={r} value={r}>
								{i18n.t(`schedule.recurrence.${r}`)}
							</MenuItem>
						))}
					</TextField>
				</div>
				<Typography variant="caption" color="textSecondary">
					{i18n.t("schedule.hint")}
				</Typography>

				<Typography variant="subtitle2" style={{ marginTop: 16 }}>
					{i18n.t("schedule.list")}
				</Typography>
				{items.length === 0 ? (
					<Typography variant="body2">{i18n.t("schedule.empty")}</Typography>
				) : (
					<List dense>
						{items.map(item => (
							<ListItem key={item.id} disableGutters>
								<ListItemText
									primary={item.body}
									primaryTypographyProps={{ noWrap: true }}
									secondary={`${new Date(item.sendAt).toLocaleString()} · ${statusLabel(item)}${
										item.lastError ? ` · ${item.lastError}` : ""
									}`}
								/>
								{item.status === "pending" && (
									<ListItemSecondaryAction>
										<IconButton size="small" onClick={() => cancel(item)}>
											<Close fontSize="small" />
										</IconButton>
									</ListItemSecondaryAction>
								)}
							</ListItem>
						))}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined" color="secondary">
					{i18n.t("schedule.close")}
				</Button>
				<Button
					onClick={save}
					disabled={saving || !body.trim() || !when}
					variant="contained"
					color="primary"
				>
					{i18n.t("schedule.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ScheduleDialog;
